import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DesignerKpiTab } from '../components/features/idt-mypage/DesignerKpiTab.js';
import { DesignerSkillEvaluationTab } from '../components/features/idt-mypage/DesignerSkillEvaluationTab.js';
import { ProtectedRoute } from '../components/auth/index.js';

/** IDT デザイナー MyPage タブ (ログイン MyPage の IDT_DESIGNER_TABS と同一)。 @graph-connects none */
const IDT_DESIGNER_TABS = [
  { id: 'kpi' as const, label: 'KPI' },
  { id: 'skill' as const, label: 'スキル評価' },
];

/** @graph-connects none */
type IdtDesignerTabId = (typeof IDT_DESIGNER_TABS)[number]['id'];

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT デザイナーの MyPage ページ (nickname 指定)。看板 karte の MyPage 導線先。ログイン MyPage と同じく KPI / スキル評価タブを表示
 * @graph-connects DesignerKpiTab [calls] デザイナー KPI MyPage 本体 (当月pt / 四半期pt表 / 担当案件)
 * @graph-connects DesignerSkillEvaluationTab [calls] デザイナーのスキル評価 (個人 vs 全体デザイン評価) タブ
 */
export const Route = createFileRoute('/idt-designer/$nickname')({
  component: IdtDesignerPage,
});

/** @graph-connects ProtectedRoute [calls] IDチーム看板と同じアクセス可否 (idtKpi) でガードする */
function IdtDesignerPage() {
  return (
    <ProtectedRoute pageKey="idtKpi">
      <IdtDesignerPageContent />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function IdtDesignerPageContent() {
  const { t } = useTranslation();
  const { nickname } = Route.useParams();
  const [activeTab, setActiveTab] = useState<IdtDesignerTabId>('kpi');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 戻るボタン + ニックネーム見出し (8bit フォント) */}
      <div className="flex items-center gap-4">
        <Link
          to="/members/idt-kpi"
          className="rounded-lg p-2 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]"
          aria-label={t('actions.back')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </Link>
        <h1
          className="text-xl font-bold text-[var(--color-text-primary)]"
          style={{ fontFamily: "'Press Start 2P', monospace" }}
        >
          {nickname}
        </h1>
      </div>

      {/* タブ (ログイン MyPage の IDT デザイナー表示と同じ KPI / スキル評価) */}
      <div
        className="flex border-b border-[var(--color-border)]"
        role="tablist"
        aria-label="マイページセクション"
      >
        {IDT_DESIGNER_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => {
              setActiveTab(tab.id);
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-[var(--color-accent-primary)] border-b-2 border-[var(--color-accent-primary)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* タブコンテンツ */}
      <div role="tabpanel">
        {activeTab === 'kpi' && <DesignerKpiTab nickName={nickname} />}
        {activeTab === 'skill' && <DesignerSkillEvaluationTab nickName={nickname} />}
      </div>
    </div>
  );
}
